import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';

export const DEFAULT_PERMISSIONS = {
  owner:   { canManageBranding: true,  canManageUsers: true,  canManageMembers: true,  canManageEvents: true,  canManageReports: true  },
  admin:   { canManageBranding: true,  canManageUsers: false, canManageMembers: true,  canManageEvents: true,  canManageReports: true  },
  manager: { canManageBranding: false, canManageUsers: false, canManageMembers: true,  canManageEvents: true,  canManageReports: true  },
  volunteer: { canManageBranding: false, canManageUsers: false, canManageMembers: false, canManageEvents: false, canManageReports: false }
};

// Register a new org owner — always written to the `staff` collection
export const registerOrgOwner = async (email, password, name, orgId) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  await sendEmailVerification(user);
  await setDoc(doc(db, 'staff', user.uid), {
    uid: user.uid,
    email,
    name,
    orgId,
    role: 'owner',
    permissions: DEFAULT_PERMISSIONS.owner,
    createdAt: new Date().toISOString()
  });
  return user;
};

// Login for staff (owner / admin / manager) — reads from the `staff` collection
export const loginOrgUser = async (email, password, expectedOrgId = null) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  const userDoc = await getDoc(doc(db, 'staff', user.uid));
  if (!userDoc.exists()) {
    await signOut(auth);
    throw new Error('Staff account not found. Please contact your administrator.');
  }

  const userData = userDoc.data();

  if (expectedOrgId && userData.orgId !== expectedOrgId) {
    await signOut(auth);
    throw new Error(`Access denied. This login is for ${expectedOrgId} staff only.`);
  }

  // Fetch org name
  let orgName = 'Organisation';
  const orgDoc = await getDoc(doc(db, 'organizations', userData.orgId));
  if (orgDoc.exists()) {
    orgName = orgDoc.data().branding?.name || orgDoc.data().profile?.name || orgName;
  }

  return { user: { uid: user.uid, email: user.email, ...userData, orgName } };
};

// Get currently signed-in staff member from the `staff` collection
export const getCurrentOrgUser = async () => {
  if (!auth.currentUser) return null;
  const userDoc = await getDoc(doc(db, 'staff', auth.currentUser.uid));
  if (!userDoc.exists()) return null;
  const userData = userDoc.data();
  const permissions = userData.permissions || DEFAULT_PERMISSIONS[userData.role] || DEFAULT_PERMISSIONS.manager;
  return { uid: auth.currentUser.uid, email: auth.currentUser.email, ...userData, permissions };
};

// Returns true if the current user has a record in the `staff` collection
export const isOrgUserAuthenticated = async () => {
  if (!auth.currentUser) return false;
  const userDoc = await getDoc(doc(db, 'staff', auth.currentUser.uid));
  return userDoc.exists();
};

export const logoutOrgUser = async () => {
  await signOut(auth);
  return true;
};

export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
  return true;
};

// Fetch all staff members for a given org
export const getOrgUsers = async (orgId) => {
  const q = query(collection(db, 'staff'), where('orgId', '==', orgId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ uid: d.id, ...d.data() }));
};

// Fetch all volunteers for a given org
export const getOrgVolunteers = async (orgId) => {
  const q = query(collection(db, 'volunteers'), where('orgId', '==', orgId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ uid: d.id, ...d.data() }));
};

// Create a new org user — routes to `staff` or `volunteers` based on role
export const createOrgUser = async (email, password, name, role, permissions, orgId) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  await sendEmailVerification(user);

  const targetCollection = role === 'volunteer' ? 'volunteers' : 'staff';
  await setDoc(doc(db, targetCollection, user.uid), {
    uid: user.uid, email, name, orgId, role, permissions,
    createdAt: new Date().toISOString()
  });
  return user;
};
